import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, createSession, detectReferrer, fetchQuestionSet, submit } from '../api/client';
import type { Followup, Mode, Question, QuestionSet } from '../api/types';
import { DEFAULT_MODE, MODE_INFO, SCALE_HINT, isMode } from '../diagnosis/labels';

type Phase = 'loading' | 'answering' | 'followup' | 'scoring' | 'failed';

interface Recorded {
  readonly value: number;
  readonly elapsedMs: number;
}

export function DiagnosisPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode: Mode = isMode(params.get('mode')) ? (params.get('mode') as Mode) : DEFAULT_MODE;
  /** 再受検（安定性の確認）。結果ページの「同じ深さでもう一度」から来たときに前回の結果 ID が付く。 */
  const retestOf = params.get('retest') ?? undefined;
  /** どの診断か（`?theme=`）。設問は全テーマ共通で、見立てるキャラだけが変わる。無ければトップへ戻す。 */
  const themeId = params.get('theme') ?? '';
  /** 過去の結果の回答を写して始める（入口ページの「前回の回答を使う」。要ログイン）。写せなかった設問だけ答える。 */
  const reuseFrom = params.get('from') ?? undefined;
  const backHref = themeId ? `/t/${encodeURIComponent(themeId)}` : '/';

  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string>('');
  const [questionSet, setQuestionSet] = useState<QuestionSet | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  /** この画面で答えた分。写した回答はサーバのセッションにあるので、ここには入れない（送り直して所要時間を潰さない）。 */
  const [answers, setAnswers] = useState<Record<string, Recorded>>({});
  /** 過去の回答を写せた設問の ID。これらは出さない。 */
  const [prefilled, setPrefilled] = useState<ReadonlySet<string>>(() => new Set());
  const [index, setIndex] = useState(0);
  const [followups, setFollowups] = useState<readonly Followup[]>([]);
  const [followupIndex, setFollowupIndex] = useState(0);
  const [followupChoices, setFollowupChoices] = useState<Record<string, 'A' | 'B'>>({});

  const shownAt = useRef<number>(Date.now());
  // 二重タップや再送で結果が増殖しないよう、クライアント側で一度だけ作る。
  const idempotencyKey = useRef<string>(`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

  /** このモードの設問のうち、まだ答えていないもの（写した回答は除く）。進捗の分母もこれ。 */
  const questions: readonly Question[] = useMemo(() => {
    if (!questionSet) return [];
    const byId = new Map(questionSet.questions.map((q) => [q.id, q]));
    return (questionSet.modes[mode]?.questionIds ?? [])
      .filter((id) => !prefilled.has(id))
      .map((id) => byId.get(id))
      .filter((q): q is Question => Boolean(q));
  }, [questionSet, mode, prefilled]);

  const finish = useCallback(
    async (payload: Parameters<typeof submit>[1], id: string = sessionId) => {
      setPhase('scoring');
      try {
        const response = await submit(id, payload);
        if (response.needsFollowup) {
          setFollowups(response.followups);
          setFollowupIndex(0);
          setPhase('followup');
          return;
        }
        if (!response.resultId) throw new ApiError('INTERNAL_ERROR', '結果を作れませんでした。');
        // 取得し直さずに済むよう、結果を持って結果ページへ移る。
        navigate(`/r/${response.resultId}`, { replace: true, state: { result: response.result } });
      } catch (cause) {
        setError(cause instanceof ApiError ? cause.message : '採点できませんでした。');
        setPhase('failed');
      }
    },
    [navigate, sessionId],
  );

  useEffect(() => {
    if (!themeId) {
      navigate('/', { replace: true });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        // 深さモードは questions.json、ビッグファイブ版は bigfive.json（別の質問紙。doc/ASSESSMENT.md §9）
        const [set, session] = await Promise.all([fetchQuestionSet(mode), createSession(mode, detectReferrer(), { themeId, retestOf, reuseFrom })]);
        if (cancelled) return;
        setQuestionSet(set);
        setSessionId(session.sessionId);
        if (session.reused) {
          // 写せた設問は出さない。全部写せたなら、答えずにそのまま採点へ
          const missing = new Set(session.reused.missingIds);
          setPrefilled(new Set((set.modes[mode]?.questionIds ?? []).filter((id) => !missing.has(id))));
          if (session.reused.missingIds.length === 0) {
            void finish({ idempotencyKey: idempotencyKey.current }, session.sessionId);
            return;
          }
        }
        shownAt.current = Date.now();
        setPhase('answering');
      } catch (cause) {
        if (cancelled) return;
        setError(cause instanceof ApiError ? cause.message : '診断をはじめられませんでした。');
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
    // finish は sessionId に依存するが、ここでは引数で渡すので初回だけでよい
  }, [mode, themeId, reuseFrom]);

  const answer = useCallback(
    (question: Question, value: number) => {
      const elapsedMs = Math.max(0, Date.now() - shownAt.current);
      shownAt.current = Date.now();

      const next = { ...answers, [question.id]: { value, elapsedMs } };
      setAnswers(next);

      const answeredCount = Object.keys(next).length;
      const isLast = answeredCount >= questions.length;

      if (isLast) {
        void finish({
          answers: Object.entries(next).map(([questionId, a]) => ({ questionId, ...a })),
          idempotencyKey: idempotencyKey.current,
        });
        return;
      }
      // 途中の回答はサーバに送らない（最後の submit で全問まとめて送る）。10 問ごとの部分保存は、読み直して続きから
      // 受ける仕組みが無いまま Cloud Run と Firestore を 1 回の診断で 4〜7 回動かしていたのでやめた
      setIndex((current) => Math.min(current + 1, questions.length - 1));
    },
    [answers, finish, questions.length],
  );

  const chooseFollowup = useCallback(
    (followup: Followup, choice: 'A' | 'B') => {
      const next = { ...followupChoices, [followup.id]: choice };
      setFollowupChoices(next);
      if (followupIndex + 1 < followups.length) {
        setFollowupIndex(followupIndex + 1);
        return;
      }
      void finish({
        followupAnswers: Object.entries(next).map(([followupId, value]) => ({ followupId, choice: value })),
        idempotencyKey: idempotencyKey.current,
      });
    },
    [finish, followupChoices, followupIndex, followups.length],
  );

  if (phase === 'loading') return <p className="page__notice">診断の準備をしています…</p>;
  if (phase === 'scoring') return <p className="page__notice">採点中——あなたに近いキャラクターを探しています…</p>;

  if (phase === 'failed') {
    return (
      <main className="page">
        <h1 className="page__title">診断を続けられませんでした</h1>
        <p className="page__lead">{error}</p>
        <div className="page__actions">
          <button className="button button--primary" type="button" onClick={() => window.location.reload()}>
            もう一度試す
          </button>
          <button className="button button--ghost" type="button" onClick={() => navigate(backHref)}>
            はじめに戻る
          </button>
        </div>
      </main>
    );
  }

  if (phase === 'followup') {
    const followup = followups[followupIndex];
    if (!followup) return <p className="page__notice">採点中…</p>;
    return (
      <main className="page quiz">
        <Progress
          current={followupIndex + 1}
          total={followups.length}
          caption="決めかねている軸を、あと少しだけ"
        />
        <p className="quiz__lead">どちらが近いですか。</p>
        <div className="choice">
          <button className="choice__button" type="button" onClick={() => chooseFollowup(followup, 'A')}>
            {followup.optionA.text}
          </button>
          <button className="choice__button" type="button" onClick={() => chooseFollowup(followup, 'B')}>
            {followup.optionB.text}
          </button>
        </div>
      </main>
    );
  }

  const question = questions[index];
  if (!question) return <p className="page__notice">設問を読み込めませんでした。</p>;
  const answered = Object.keys(answers).length;
  const current = answers[question.id];

  return (
    <main className="page quiz">
      <Progress
        current={answered + (current ? 0 : 1)}
        total={questions.length}
        caption={`${MODE_INFO.find((m) => m.mode === mode)?.label ?? ''}・${prefilled.size > 0 ? `残り${questions.length}問` : `全${questions.length}問`}`}
      />
      {prefilled.size > 0 && index === 0 && !current && (
        <p className="quiz__lead">前回の回答 {prefilled.size} 問を使います。足りない {questions.length} 問だけ答えてください。</p>
      )}

      <h1 className="quiz__question">{question.text}</h1>

      <ol className="likert">
        {SCALE_HINT.map((label, i) => {
          const value = i + 1;
          return (
            <li key={label}>
              <button
                className={`likert__button${current?.value === value ? ' likert__button--selected' : ''}`}
                type="button"
                onClick={() => answer(question, value)}
              >
                {label}
              </button>
            </li>
          );
        })}
      </ol>

      <div className="quiz__nav">
        <button
          className="button button--ghost"
          type="button"
          disabled={index === 0}
          onClick={() => { shownAt.current = Date.now(); setIndex(index - 1); }}
        >
          ひとつ戻る
        </button>
      </div>
    </main>
  );
}

/** 進捗バーは常時表示する。離脱率に最も効く（doc/assessment.md §3.4）。 */
function Progress({ current, total, caption }: { current: number; total: number; caption: string }) {
  const percent = Math.min(100, Math.round((current / Math.max(total, 1)) * 100));
  return (
    <div className="progress">
      <div className="progress__bar" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <span className="progress__fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="progress__caption">
        {caption}　<strong>{Math.min(current, total)}</strong> / {total}
      </p>
    </div>
  );
}
